import { BreadcrumbsNavigation } from "@/components/common/BreadcrumbsNavigation/BreadcrumbsNavigation"
import { Loading } from "@/components/common/Loading/Loading"
import {
  SplitView,
  SplitViewSection,
} from "@/components/common/SplitView/SplitView"
import { SplitViewQuestionHeader } from "@/components/common/SplitView/SplitViewQuestionHeader"
import { toast } from "@/components/common/Toast"
import { Typography } from "@/components/common/Typography/Typography"
import { CollapsibleQuestionnaireList } from "@/components/feature/CollapsibleQuestionnaireList/CollapsibleQuestionnaireList"
import { QuestionItem } from "@/components/feature/CollapsibleQuestionnaireList/QuestionItem/QuestionItem"
import { AnsweringContextDialog } from "@/components/feature/Modals/AnsweringContextDialog/AnsweringContextDialog"
import { ContinueLaterModal } from "@/components/feature/Modals/ContinueLaterModal/ContinueLaterModal"
import type { SelectDocumentsDialogSelection } from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import {
  readCompanyProfilePreference,
  SelectDocumentsDialog,
} from "@/components/feature/Modals/SelectDocumentsDialog/SelectDocumentsDialog"
import { QuestionnaireHeaderActions } from "@/components/feature/QuestionnaireHeaderActions/QuestionnaireHeaderActions"
import { Layout } from "@/components/layout"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  ONBOARDING_DEMO_EXTRACTION_FLAG,
  ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG,
  ONBOARDING_DEMO_QUESTIONNAIRE_ID,
  ONBOARDING_EXTRACTION_STEP_ID,
  ONBOARDING_GENERATE_STEP_ID,
  ONBOARDING_QUESTION_ANSWERING_STEP_ID,
  useOnboarding,
  useOnboardingQuestionnaireData,
  useOnboardingQuestionnaireDocument,
} from "@/features/onboarding"
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs/useBreadcrumbs"
import { useHasSelectableQuestionnaireDocuments } from "@/hooks/useHasSelectableQuestionnaireDocuments/useHasSelectableQuestionnaireDocuments"
import { usePermissionGuard } from "@/hooks/usePermissionGuard"
import { useQuestionnaireAutofill } from "@/hooks/useQuestionnaireAutofill/useQuestionnaireAutofill"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { ErrorPage } from "@/pages/ErrorPage"
import { AnalyticsEvent, trackEvent } from "@/services/analytics/amplitude"
import { isQuestionnaireNotFound } from "@/services/api/questionnaireQueryPolicy"
import {
  useGetQuestionnaireAnsweringContext,
  useSaveQuestionnaireFilterConfig,
} from "@/services/api/questionnaireService"
import { isExcelFileName } from "@/utils/fileUtils"
import { isLimitError } from "@/utils/limitErrors"
import { reduceNumTotalQuestions } from "@/utils/questionnaire/reduceQuestions"
import { WorkbookInstance } from "@fortune-sheet/react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router"
import { ExcelPreviewPanel } from "../ExcelAnswersPage/ExcelPreviewPanel"

export const ExcelQuestionsPage = () => {
  const { translate } = useTranslation()
  const taggingFlowEnabled = useFeatureFlag("KB_TAGGING_FLOW")
  const answeringContextEnabled = useFeatureFlag("ANSWERING_CONTEXT")
  const [isAnsweringContextOpen, setIsAnsweringContextOpen] = useState(false)
  const { hasSelectableQuestionnaireDocuments } =
    useHasSelectableQuestionnaireDocuments(taggingFlowEnabled)
  const { questionnaireId } = useParams<Record<string, string | undefined>>()
  const workbookRef = useRef<WorkbookInstance | null>(null)
  const lastTourBackTimestampRef = useRef<number | null>(null)
  const navigate = useNavigate()
  const {
    handleHighlightedAction,
    getCurrentTour,
    currentStepIndex,
    isActive,
  } = useOnboarding()
  const location = useLocation()

  // Modal states
  const [isContinueLaterModalOpen, setIsContinueLaterModalOpen] =
    useState(false)
  const [isSelectDocumentsOpen, setIsSelectDocumentsOpen] = useState(false)
  const [numTotalQuestions, setNumTotalQuestions] = useState(0)
  const [isDemoExtractionVisible, setIsDemoExtractionVisible] = useState(false)
  const [demoExtractionKey, setDemoExtractionKey] = useState(0)
  const [isDemoQuestionAnsweringVisible, setIsDemoQuestionAnsweringVisible] =
    useState(false)
  const [demoQuestionAnsweringKey, setDemoQuestionAnsweringKey] = useState(0)
  const { breadcrumbs } = useBreadcrumbs()
  const [answerGenerationInProgress, setAnswerGenerationInProgress] =
    useState(false)
  const [selectedDocumentsCount, setSelectedDocumentsCount] = useState(0)
  const [savedSelectedTagIds, setSavedSelectedTagIds] = useState<string[]>([])
  const [excludeExpired, setExcludeExpired] = useState(true)
  const [useCompanyProfile, setUseCompanyProfile] = useState(
    readCompanyProfilePreference,
  )

  const isOnboardingGenerateStep = useMemo(() => {
    if (!isActive) return false
    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]
    return currentStep?.id === ONBOARDING_GENERATE_STEP_ID
  }, [currentStepIndex, getCurrentTour, isActive])

  // Questionnaire data fetching
  const {
    data: questionnaire,
    isPending: isQuestionnairePending,
    isFetched: isQuestionnaireFetched,
    isError: isQuestionnaireError,
    error: questionnaireError,
    isDemo: isDemoQuestionnaire,
    refetch: refetchQuestionnaire,
  } = useOnboardingQuestionnaireData(questionnaireId)

  const hasGeneratedAnswers = !!questionnaire?.sections.some((section) =>
    section.responsePairs.some((pair) => !!pair.response),
  )

  // Answering context only applies to v2 (ingested) questionnaires; a legacy
  // questionnaire's saved context would never reach the answering pipeline.
  // The answering-context endpoints require QUEST_RUN; viewers without it get neither the
  // button nor the query behind it.
  const { canRun: canRunQuestionnaire } = usePermissionGuard(
    PERMISSIONS.QUEST_RUN,
  )
  const showAnsweringContextButton =
    answeringContextEnabled &&
    canRunQuestionnaire &&
    questionnaire?.ingestionStatus != null

  const { data: answeringContext } = useGetQuestionnaireAnsweringContext(
    questionnaireId,
    showAnsweringContextButton,
  )
  const answeringContextSaved = !!(
    answeringContext?.text || answeringContext?.terms.length
  )

  const { runAutofillWithRedirect, isPending: isAutofillTriggerPending } =
    useQuestionnaireAutofill({
      questionnaireId,
      questionnaire,
      navigate,
      refetchQuestionnaire,
    })
  const saveQuestionnaireFilterConfig = useSaveQuestionnaireFilterConfig()

  // Without cell positions the document endpoint serves a PDF summary instead of
  // the workbook, so such questionnaires belong on the plain questions page.
  const lacksPositionData = !!questionnaire && !questionnaire.hasPositionData

  // Excel file fetching
  const shouldFetchDocument =
    !!questionnaireId &&
    !!questionnaire?.hasPositionData &&
    isExcelFileName(questionnaire.fileName) &&
    !isQuestionnaireNotFound(questionnaireError)

  const {
    data: excelFileUrl,
    isPending: isExcelFilePending,
    isError: isExcelFileError,
  } = useOnboardingQuestionnaireDocument(questionnaireId, shouldFetchDocument, {
    demoDocumentVariant: "original",
  })

  useEffect(() => {
    if (questionnaireId && lacksPositionData)
      navigate(`/questionnaire/${questionnaireId}/questions`, { replace: true })
  }, [lacksPositionData, navigate, questionnaireId])

  // Handle questionnaire data loading
  useEffect(() => {
    if (isQuestionnaireError && !isQuestionnaireNotFound(questionnaireError)) {
      toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    } else if (isQuestionnaireFetched && questionnaire) {
      setNumTotalQuestions(reduceNumTotalQuestions(questionnaire))
    }
  }, [isQuestionnaireFetched, questionnaire, isQuestionnaireError, translate])

  useEffect(() => {
    const shouldShowExtraction =
      isDemoQuestionnaire &&
      sessionStorage.getItem(ONBOARDING_DEMO_EXTRACTION_FLAG) === "true"

    if (!shouldShowExtraction) {
      return
    }

    sessionStorage.removeItem(ONBOARDING_DEMO_EXTRACTION_FLAG)
    setDemoExtractionKey((prev) => prev + 1)
    setIsDemoExtractionVisible(true)
  }, [isDemoQuestionnaire, location.key])

  // Event handlers
  const handleOpenContinueLaterModal = () => {
    setIsContinueLaterModalOpen(true)
  }

  const handleDemoExtractionComplete = useCallback(() => {
    setIsDemoExtractionVisible(false)

    if (!isActive) {
      return
    }

    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]

    if (currentStep?.id === ONBOARDING_EXTRACTION_STEP_ID) {
      handleHighlightedAction("question-extraction-loading")
    }
  }, [currentStepIndex, getCurrentTour, handleHighlightedAction, isActive])

  const startDemoQuestionAnsweringAnimation = useCallback(() => {
    setDemoQuestionAnsweringKey((prev) => prev + 1)
    setIsDemoQuestionAnsweringVisible(true)
  }, [])

  useEffect(() => {
    const shouldShowQuestionAnswering =
      isDemoQuestionnaire &&
      sessionStorage.getItem(ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG) === "true"

    if (!shouldShowQuestionAnswering) {
      return
    }

    sessionStorage.removeItem(ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG)
    startDemoQuestionAnsweringAnimation()
  }, [isDemoQuestionnaire, location.key, startDemoQuestionAnsweringAnimation])

  useEffect(() => {
    const tourBackState =
      (location.state as {
        tourBackTimestamp?: number
        tourBackFromStepId?: string
      } | null) ?? undefined

    const timestamp = tourBackState?.tourBackTimestamp
    if (!timestamp || lastTourBackTimestampRef.current === timestamp) {
      return
    }

    lastTourBackTimestampRef.current = timestamp

    if (
      tourBackState?.tourBackFromStepId ===
      ONBOARDING_QUESTION_ANSWERING_STEP_ID
    ) {
      setIsDemoQuestionAnsweringVisible(false)
      sessionStorage.removeItem(ONBOARDING_DEMO_QUESTION_ANSWERING_FLAG)
    }
  }, [location.state])

  const handleDemoQuestionAnsweringComplete = useCallback(() => {
    setIsDemoQuestionAnsweringVisible(false)

    if (!isActive) {
      return
    }

    const currentTour = getCurrentTour()
    const currentStep = currentTour?.steps?.[currentStepIndex]

    if (currentStep?.id === ONBOARDING_QUESTION_ANSWERING_STEP_ID) {
      handleHighlightedAction("question-answering-loading", {
        performNavigation: true,
      })
    }
  }, [currentStepIndex, getCurrentTour, handleHighlightedAction, isActive])

  const handleTriggerAutofill = async () => {
    const isDemoFlow =
      isDemoQuestionnaire ||
      questionnaireId === ONBOARDING_DEMO_QUESTIONNAIRE_ID

    if (isDemoFlow) {
      if (isOnboardingGenerateStep) {
        startDemoQuestionAnsweringAnimation()
        handleHighlightedAction("generate-answers-button", {
          performNavigation: true,
        })
      } else {
        toast.warning(translate("ONBOARDING/DEMO_ACTION_DISABLED"))
      }
      return
    }

    if (questionnaire && questionnaireId) {
      trackEvent(AnalyticsEvent.AUTO_FILL_STARTED, {
        questionnaire_id: questionnaireId,
        question_count: numTotalQuestions,
        source: "EXCEL",
      })
      try {
        setAnswerGenerationInProgress(true)
        await runAutofillWithRedirect(useCompanyProfile)
      } catch (error) {
        // The axios interceptor already showed the limit toast with its upgrade action
        if (!isLimitError(error)) {
          toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_AUTOFILL_ERROR"))
        }
        trackEvent(AnalyticsEvent.AUTO_FILL_FAILED, {
          questionnaire_id: questionnaireId,
          source: "EXCEL",
        })
      } finally {
        setAnswerGenerationInProgress(false)
      }
    }
  }

  const handleSaveDocumentSelection = async (
    selection: SelectDocumentsDialogSelection,
  ) => {
    try {
      await saveQuestionnaireFilterConfig.mutateAsync({
        questionnaireId,
        body: {
          categoryFilters: selection.categoryFilters,
          includeUnclassified: selection.includeUnclassified,
          excludeExpired: selection.excludeExpired,
        },
      })

      setSelectedDocumentsCount(selection.selectedFiles.length)
      setSavedSelectedTagIds(selection.selectedTagIds)
      setExcludeExpired(selection.excludeExpired)
      setUseCompanyProfile(selection.useCompanyProfile)
    } catch (error) {
      // The axios interceptor already showed the limit toast with its upgrade action
      if (!isLimitError(error)) {
        toast.error(translate("ERROR/SOMETHING_WENT_WRONG"))
      }
      throw new Error("Failed to save questionnaire filter config", {
        cause: error,
      })
    }
  }

  const handleClearDocumentSelection = () => {
    setSelectedDocumentsCount(0)
    setSavedSelectedTagIds([])
    setExcludeExpired(true)
  }

  if (isQuestionnaireNotFound(questionnaireError))
    return <ErrorPage variant="not-found" withLayout />

  if (lacksPositionData) return null

  if (!questionnaireId) {
    toast.error(translate("QUESTIONNAIRE_QUESTIONS/TOAST_GET_ERROR"))
    return null
  }

  if (isDemoExtractionVisible) {
    return (
      <Layout
        defaultSidebarState={"closed"}
        headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
        headerRightElement={null}
      >
        <div className="flex h-full flex-col items-center justify-center">
          <div className="p-2.5" id="question-extraction-loading">
            <Loading
              key={`demo-extraction-${demoExtractionKey}`}
              autoIncrementIntervalMs={30}
              text={translate(
                "QUESTIONNAIRE_QUESTIONS/TIMER_EXTRACTING_QUESTIONS",
              )}
              onEndReached={handleDemoExtractionComplete}
            />
          </div>
        </div>
      </Layout>
    )
  }

  if (isDemoQuestionAnsweringVisible) {
    return (
      <Layout
        defaultSidebarState={"closed"}
        headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
        headerRightElement={null}
      >
        <div className="flex h-full flex-col items-center justify-center">
          <div className="p-2.5" id="question-answering-loading">
            <Loading
              key={`demo-question-answering-${demoQuestionAnsweringKey}`}
              autoIncrementIntervalMs={30}
              text={translate(
                "QUESTIONNAIRE_QUESTIONS/TIMER_GENERATING_ANSWERS",
              )}
              onEndReached={handleDemoQuestionAnsweringComplete}
            />
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout
      defaultSidebarState={"closed"}
      headerLeftElement={<BreadcrumbsNavigation breadcrumbs={breadcrumbs} />}
      headerRightElement={
        <QuestionnaireHeaderActions
          continueLaterLabel={translate("QUESTIONNAIRE/CONTINUE_LATER")}
          selectDocumentsLabel={translate("SELECT_DOCUMENTS/OPEN_BUTTON")}
          showSelectDocumentsButton={hasSelectableQuestionnaireDocuments}
          selectDocumentsBadgeCount={selectedDocumentsCount}
          generateAnswersLabel={translate(
            "QUESTIONNAIRE_QUESTIONS/GENERATE_ANSWERS",
          )}
          onContinueLater={handleOpenContinueLaterModal}
          onSelectDocuments={() => setIsSelectDocumentsOpen(true)}
          onGenerateAnswers={handleTriggerAutofill}
          isGenerateDisabled={
            isQuestionnairePending ||
            isAutofillTriggerPending ||
            answerGenerationInProgress ||
            (isDemoQuestionnaire && !isOnboardingGenerateStep)
          }
          generateButtonElementId="generate-answers-button"
          showGenerateLoader={answerGenerationInProgress}
          showAnsweringContextButton={showAnsweringContextButton}
          answeringContextLabel={translate("ANSWERING_CONTEXT/OPEN_BUTTON")}
          onAnsweringContext={() => setIsAnsweringContextOpen(true)}
          answeringContextSaved={answeringContextSaved}
        />
      }
    >
      {isContinueLaterModalOpen && (
        <ContinueLaterModal
          setIsContinueLaterModalOpen={setIsContinueLaterModalOpen}
        />
      )}

      {hasSelectableQuestionnaireDocuments ? (
        <SelectDocumentsDialog
          questionnaireId={questionnaireId}
          open={isSelectDocumentsOpen}
          onOpenChange={setIsSelectDocumentsOpen}
          initialSelectedTagIds={savedSelectedTagIds}
          initialHideExpired={excludeExpired}
          onConfirm={handleSaveDocumentSelection}
          onClearSelection={handleClearDocumentSelection}
        />
      ) : null}

      {answeringContextEnabled ? (
        <AnsweringContextDialog
          questionnaireId={questionnaireId}
          open={isAnsweringContextOpen}
          onOpenChange={setIsAnsweringContextOpen}
          hasAnswers={hasGeneratedAnswers}
          onRegenerate={() => {
            void handleTriggerAutofill()
          }}
        />
      ) : null}

      <SplitView>
        <SplitViewSection>
          <ExcelPreviewPanel
            excelFileUrl={excelFileUrl}
            isLoading={isExcelFilePending}
            isError={isExcelFileError}
            workbookRef={workbookRef}
          />
        </SplitViewSection>

        <SplitViewSection className="px-M">
          <div className="flex h-full flex-col">
            <SplitViewQuestionHeader
              titleKey="BREADCRUMBS_NAVIGATION/TITLE_QUESTIONS"
              countLabelKey="QUESTIONNAIRE_QUESTIONS/COUNT_TOTAL_QUESTIONS"
              countValueDisplay={
                <Typography
                  as="span"
                  variant="textM"
                  className="text-yellow1"
                  dataTestId={"questionnaire-total"}
                >
                  {numTotalQuestions}
                </Typography>
              }
            />
            <div className="flex-1 overflow-y-auto pt-XS pr-4">
              {questionnaire?.sections.map((section, index) => {
                return (
                  <CollapsibleQuestionnaireList
                    key={`${section.id}-${index}`}
                    dataTestId="questionnaire-section"
                    sectionIndex={index}
                    sectionTitle={section.name}
                    sectionCountDisplay={
                      <Typography as="p" variant="textM">
                        {translate(
                          "QUESTIONNAIRE_QUESTIONS/COUNT_SECTION_QUESTIONS",
                        )}{" "}
                        <Typography
                          as="span"
                          variant="textM"
                          className="text-yellow1"
                        >
                          {section.responsePairs.length}
                        </Typography>
                      </Typography>
                    }
                  >
                    {section.responsePairs.map((pair, index) => {
                      return (
                        <QuestionItem
                          key={`${pair.id}-${index}`}
                          questionnaireId={questionnaireId}
                          responsePair={pair}
                          editEnabled={false}
                        />
                      )
                    })}
                  </CollapsibleQuestionnaireList>
                )
              })}
            </div>
          </div>
        </SplitViewSection>
      </SplitView>
    </Layout>
  )
}
